import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { Link } from 'react-router';

import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, Eye, EyeOff, XCircle } from 'lucide-react';

import { useCheckResetTokenMutation, useResetPasswordMutation } from '@/features/auth/store/api';
import { PrimaryButton, GlassCard, Input, FormField } from '@/lib/ui';
import { extractServerError, notify } from '@/lib/utils';
import { ROUTES } from '@/routes';

import { useUrlToken } from '../hooks';
import { ResetPasswordFormSchema } from '../schemas';
import type { ResetPasswordFormValues } from '../types';

const authFieldInputClass =
  'border-white/10 bg-black/40 text-white placeholder:text-white/45 ring-offset-background';

const GENERIC_EXPIRED_MESSAGE = 'This reset link is no longer valid.';

type ViewStatus = 'checking' | 'form' | 'expired' | 'success';

// ── Component ─────────────────────────────────────────────────────────────────

export function ResetPasswordView() {
  const token = useUrlToken();

  const [checkResetToken] = useCheckResetTokenMutation();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const [status, setStatus] = useState<ViewStatus>('checking');
  const [expiredMessage, setExpiredMessage] = useState(GENERIC_EXPIRED_MESSAGE);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({ resolver: zodResolver(ResetPasswordFormSchema) });

  useEffect(() => {
    if (!token) {
      setExpiredMessage(GENERIC_EXPIRED_MESSAGE);
      setStatus('expired');
      return;
    }
    checkResetToken({ token })
      .unwrap()
      .then(() => setStatus('form'))
      .catch((err) => {
        setExpiredMessage(extractServerError(err, GENERIC_EXPIRED_MESSAGE));
        setStatus('expired');
      });
    // checkResetToken is stable — intentionally omitted from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSubmit = async (data: ResetPasswordFormValues) => {
    try {
      await resetPassword({ token, new_password: data.password }).unwrap();
      setStatus('success');
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code;
      if (code === 'ERR_LINK_EXPIRED') {
        setExpiredMessage(extractServerError(err, GENERIC_EXPIRED_MESSAGE));
        setStatus('expired');
        return;
      }
      notify.error(extractServerError(err, 'Failed to reset password. Please try again.'));
    }
  };

  return (
    <div className="w-full max-w-md space-y-6">
      <div className="flex justify-center">
        <img src="/moveris-logo-dark.svg" alt="Moveris" className="h-10 w-auto" />
      </div>

      <GlassCard padding="lg" className="!bg-[var(--color-glass-bg)] space-y-6">
        {status === 'checking' && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <div
              className="size-10 animate-spin rounded-full border-4 border-white/20 border-t-white"
              role="status"
              aria-label="Checking your reset link"
            />
            <p className="text-sm text-white/60">Checking your reset link…</p>
          </div>
        )}

        {status === 'expired' && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <XCircle className="size-12 text-destructive" aria-hidden="true" />
            <div className="space-y-1">
              <h1 className="text-xl font-semibold text-white">Link no longer valid</h1>
              <p className="text-sm text-white/60">{expiredMessage}</p>
            </div>
            <Link
              to={ROUTES.forgotPassword}
              className="text-sm font-medium text-white hover:underline"
            >
              Request a new reset link
            </Link>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <CheckCircle2 className="size-12 text-success" aria-hidden="true" />
            <div className="space-y-1">
              <h1 className="text-xl font-semibold text-white">Password updated</h1>
              <p className="text-sm text-white/60">
                Your password has been changed. Sign in with your new password.
              </p>
            </div>
            <PrimaryButton variant="success" asChild>
              <Link to={ROUTES.login}>Go to sign in</Link>
            </PrimaryButton>
          </div>
        )}

        {status === 'form' && (
          <>
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold text-white">Set a new password</h1>
              <p className="text-sm text-white/60">Choose a new password for your account.</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
              <FormField
                label="New password"
                htmlFor="reset-password"
                error={errors.password?.message}
                labelClassName="!text-white !font-semibold"
              >
                <div className="relative">
                  <Input
                    id="reset-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your new password"
                    autoComplete="new-password"
                    className={authFieldInputClass}
                    {...register('password')}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </FormField>

              <FormField
                label="Confirm new password"
                htmlFor="reset-confirm-password"
                error={errors.confirmPassword?.message}
                labelClassName="!text-white !font-semibold"
              >
                <div className="relative">
                  <Input
                    id="reset-confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Enter your new password"
                    autoComplete="new-password"
                    className={authFieldInputClass}
                    {...register('confirmPassword')}
                  />
                  <button
                    type="button"
                    aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                    onClick={() => setShowConfirm((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                  >
                    {showConfirm ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </FormField>

              <PrimaryButton type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Updating…' : 'Update password'}
              </PrimaryButton>
            </form>
          </>
        )}
      </GlassCard>
    </div>
  );
}
